// src/features/mech/plastics/questions/PartNameQuestion.jsx
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Box } from "@mui/material";
import PerlTextField from "../../../../shared/components/PerlTextField";
import PerlLabelMedium from "../../../../shared/components/PerlLabelMedium";
import PerlChip from "../../../../shared/components/PerlChip";

/**
 * SelectProcessQuestion Component
 *
 * Custom question for Mechanical Sheet Metal: captures the process type
 * Only allows predefined process options from chips
 *
 * @param {string} value - Current process selection
 * @param {function} onChange - Callback when the process changes
 * @param {string} [error] - Optional error message
 * @param {string} [success] - Optional success/help message
 * @param {function} [onNextStep] - Optional callback to proceed to next step on Enter key
 * @param {number} [currentStep] - Current step index
 */
const SelectProcessQuestion = forwardRef(({ value, onChange, error, success, onNextStep, currentStep }, ref) => {
  const [validationError, setValidationError] = useState("");
  const [validationAttempted, setValidationAttempted] = useState(false);

  // Process options - only these are allowed
  const processOptions = ["Stage", "Progressive", "Turret Punch", "Laser Cutting"];

  // Validate process selection
  const validate = () => {
    setValidationAttempted(true);
    if (!value || value.trim() === "") {
      setValidationError("Please select a process");
      return false;
    }
    
    const trimmedValue = value.trim();
    // Compare with trimmed options to handle any whitespace issues
    const isValid = processOptions.some(option => option.trim().toLowerCase() === trimmedValue.toLowerCase());
    
    if (!isValid) {
      setValidationError("Please select a process from the suggested options");
      return false;
    }
    
    setValidationError("");
    return true;
  };

  // Expose validate method to parent
  useImperativeHandle(ref, () => ({
    validate,
    isValid: () => {
      if (!value || value.trim() === "") return false;
      const trimmedValue = value.trim();
      // Compare with trimmed options to handle any whitespace issues
      return processOptions.some(option => option.trim().toLowerCase() === trimmedValue.toLowerCase());
    },
  }));

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && onNextStep && value && value.trim() !== "") {
      e.preventDefault();
      onNextStep();
    }
  };

  // Handle chip selection
  const handleProcessChipClick = (process) => {
    onChange(process);
    // Clear validation error when a valid chip is selected
    if (validationError) {
      setValidationError("");
      setValidationAttempted(false);
    }
  };

  // Show error if validation was attempted and failed
  const displayError = validationAttempted ? validationError : error;
  const hasError = Boolean(displayError);

  return (
    <>
      <PerlLabelMedium sx={{ mb: 2, display: "block" }}>Enter Process:</PerlLabelMedium>
      <PerlTextField
        fullWidth
        placeholder="Select a process from the options below"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          // Clear validation error when user starts typing
          if (validationError) {
            setValidationError("");
            setValidationAttempted(false);
          }
        }}
        onKeyDown={handleKeyDown}
        error={hasError}
        helperText={displayError}
      />

      {/* Suggested Process Chips */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
        <PerlLabelMedium sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 0.5 }}>
          Suggested Process:
        </PerlLabelMedium>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            gap: 1,
          }}
        >
          {processOptions.map((process) => (
            <PerlChip
              key={process}
              label={process}
              selected={value && value.trim().toLowerCase() === process.trim().toLowerCase()}
              onClick={() => handleProcessChipClick(process)}
      />
          ))}
        </Box>
      </Box>

      {success && (
        <PerlLabelMedium sx={{ mt: 1, color: "success.main" }}>{success}</PerlLabelMedium>
      )}
    </>
  );
});

SelectProcessQuestion.displayName = "SelectProcessQuestion";

export default SelectProcessQuestion;

